import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Button, CheckChip, Segmented, Skeleton, memberColor } from '@koyomi/ui';
import { useAuthStore } from '../../app/auth-store.ts';
import { useBusiness } from '../../lib/queries.ts';
import { useRealtime } from '../../lib/socket.ts';
import { useCalendar, useCalendarMembers, useWaitlist, type AppointmentDto } from '../../lib/calendar-api.ts';
import { dayOf, endOfDayIso, fmtDay, shiftDay, startOfDayIso, startOfMonth, startOfWeek, timeOf, todayIn } from '../../lib/time.ts';
import { DayView, MonthView, WeekView } from './views.tsx';
import { AppointmentPanel } from './AppointmentPanel.tsx';
import { BlockTimeModal, NewAppointmentModal } from './dialogs.tsx';
import { InstructionModal } from './InstructionModal.tsx';
import './calendar.css';

type View = 'day' | 'week' | 'month';
/** Agenda mostra só o que ocupa horário; cancelados e versões antigas de remarcados só com "Mostrar cancelados". */
const ACTIVE = new Set(['CONFIRMED', 'DONE', 'NO_SHOW']);
const CANCELLED_KEY = 'koyomi.calendar.showCancelled';
const readShowCancelled = () => { try { return localStorage.getItem(CANCELLED_KEY) === '1'; } catch { return false; } };

export function CalendarPage() {
  const { t, i18n } = useTranslation(['calendar', 'common']);
  const business = useBusiness();
  const me = useAuthStore((s) => s.businesses.find((b) => b.id === s.businessId));
  const tz = business.data?.timezone ?? 'America/Sao_Paulo';
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [params] = useSearchParams();
  const [view, setView] = useState<View>(params.get('day') ? 'day' : isMobile ? 'day' : 'week');
  const dayParam = /^\d{4}-\d{2}-\d{2}$/.test(params.get('day') ?? '') ? params.get('day')! : null;
  const [day, setDay] = useState(() => dayParam ?? todayIn(tz));
  // "Hoje" no fuso do negócio: só dá para saber depois de carregar o negócio (antes disso o fuso é um chute).
  const [dayTouched, setDayTouched] = useState(!!dayParam);
  useEffect(() => { if (business.data?.timezone && !dayTouched) setDay(todayIn(business.data.timezone)); }, [business.data?.timezone, dayTouched]);
  const pickDay = (d: string) => { setDayTouched(true); setDay(d); };
  const waitlist = useWaitlist();
  const dayQueues = (waitlist.data?.queues ?? []).filter((q) => dayOf(q.desiredStart, tz) === day);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [picked, setSelected] = useState<AppointmentDto | null>(null);
  const showQueues = !picked && view === 'day' && !!business.data?.featureFlags.waitlist && dayQueues.length > 0;
  const [dialog, setDialog] = useState<'none' | 'new' | 'block' | 'instruction'>('none');
  const [newAt, setNewAt] = useState<{ day: string; time?: string; memberId?: string } | null>(null);
  const members = useCalendarMembers();

  const range = useMemo(() => {
    if (view === 'day') return { from: day, to: day };
    if (view === 'week') { const s = startOfWeek(day); return { from: s, to: shiftDay(s, 6) }; }
    const s = startOfMonth(day);
    return { from: shiftDay(startOfWeek(s), 0), to: shiftDay(startOfWeek(s), 41) };
  }, [view, day]);
  const cal = useCalendar(startOfDayIso(range.from, tz), endOfDayIso(range.to, tz), selectedMembers);
  const [showCancelled, setShowCancelledState] = useState(readShowCancelled);
  const setShowCancelled = (v: boolean) => { setShowCancelledState(v); try { localStorage.setItem(CANCELLED_KEY, v ? '1' : '0'); } catch { /* sem storage */ } };
  const calData = useMemo(() => (cal.data && !showCancelled ? { ...cal.data, appointments: cal.data.appointments.filter((a) => ACTIVE.has(a.status)) } : cal.data), [cal.data, showCancelled]);
  // Tempo real: qualquer mudança na agenda (IA, equipe, lembretes, lista de espera) recarrega a tela aberta.
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  useRealtime({
    'calendar.changed': () => { for (const k of ['calendar', 'waitlist', 'slots']) qc.invalidateQueries({ queryKey: [businessId, k] }); },
    'waitlist.changed': () => qc.invalidateQueries({ queryKey: [businessId, 'waitlist'] }),
  });
  // O painel mostra a versão mais recente do agendamento aberto (ex.: a IA cancelou enquanto o painel estava aberto).
  const selected = picked ? (cal.data?.appointments.find((a) => a.id === picked.id) ?? picked) : null;
  const visibleMembers = (members.data ?? []).filter((m) => selectedMembers.length === 0 || selectedMembers.includes(m.id));
  const isAdmin = me?.role === 'ADMIN';

  const step = (n: number) => pickDay(view === 'day' ? shiftDay(day, n) : view === 'week' ? shiftDay(day, 7 * n) : (() => { const [y, m] = day.split('-').map(Number); return new Date(Date.UTC(y!, m! - 1 + n, 1)).toISOString().slice(0, 10); })());
  const title = view === 'day'
    ? fmtDay(day, i18n.language, { weekday: 'long', day: 'numeric', month: 'long' })
    : view === 'week'
      ? t('calendar:rangeTitle', { from: fmtDay(range.from, i18n.language, { day: 'numeric' }), to: fmtDay(range.to, i18n.language, { day: 'numeric', month: 'long' }) })
      : fmtDay(day, i18n.language, { month: 'long', year: 'numeric' });
  const count = (cal.data?.appointments ?? []).filter((a) => a.status === 'CONFIRMED' || a.status === 'DONE' || a.status === 'NO_SHOW').length;
  const openNew = (p?: { day: string; time?: string; memberId?: string }) => { setNewAt(p ?? { day }); setDialog('new'); };

  return (
    <>
      <header className="k-page-header">
        <div>
          <h1 className="k-page-title">{title.charAt(0).toUpperCase() + title.slice(1)}</h1>
          <div className="k-page-subtitle">{t('calendar:summary', { scope: selectedMembers.length === 0 ? (isAdmin ? t('calendar:allAgendas') : t('calendar:myTitle')) : visibleMembers.map((m) => m.name.split(' ')[0]).join(', '), count })}</div>
        </div>
        <div className="k-page-actions">
          {isAdmin && <Button onClick={() => setDialog('block')}>{t('calendar:actions.block')}</Button>}
          <Button onClick={() => openNew()}>{t('calendar:actions.new')}</Button>
          <Button variant="primary" onClick={() => setDialog('instruction')}>{t('calendar:actions.instruction')}</Button>
        </div>
      </header>
      <div className="cal-toolbar">
        <span className="cal-nav"><Button size="sm" onClick={() => step(-1)} aria-label="prev">‹</Button><Button size="sm" onClick={() => pickDay(todayIn(tz))}>{t('calendar:today')}</Button><Button size="sm" onClick={() => step(1)} aria-label="next">›</Button></span>
        <Segmented value={view} onChange={setView} options={(['day', 'week', 'month'] as View[]).map((v) => ({ value: v, label: t(`calendar:views.${v}`) }))} />
        <CheckChip on={showCancelled} onToggle={() => setShowCancelled(!showCancelled)}>{t('calendar:showCancelled')}</CheckChip>
        {(members.data ?? []).length > 1 && (
          <div className="cal-members">
            {(members.data ?? []).map((m) => <CheckChip key={m.id} on={selectedMembers.includes(m.id)} dot={memberColor(m.colorIndex).strong} onToggle={() => setSelectedMembers((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]))}>{m.name.split(' ')[0]}</CheckChip>)}
          </div>
        )}
      </div>
      <div className={`cal-layout ${selected || showQueues ? 'cal-layout--panel' : ''}`}>
        <div>
          {business.isLoading || members.isLoading || cal.isLoading ? <div className="k-card k-card--padded"><Skeleton lines={10} height={18} /></div> : business.data && calData && (
            view === 'day' ? <DayView day={day} tz={tz} data={calData} members={visibleMembers} business={business.data} selectedId={selected?.id ?? null} onSelect={setSelected} onEmptyClick={(memberId, time) => openNew({ day, time, memberId })} />
              : view === 'week' ? <WeekView from={range.from} tz={tz} data={calData} members={visibleMembers} business={business.data} selectedId={selected?.id ?? null} onSelect={setSelected} onDayClick={(d) => { pickDay(d); setView('day'); }} />
                : <MonthView month={day} tz={tz} data={calData} members={visibleMembers} onDayClick={(d) => { pickDay(d); setView('day'); }} />
          )}
        </div>
        {showQueues && (
          <div className="cal-queues" style={{ marginTop: 0 }}>
            <div className="k-section-label">{t('calendar:waitlist.calendar.title')}</div>
            {dayQueues.map((q) => <Link key={q.key} to="/fila" className="cal-queues__row"><strong>{timeOf(q.desiredStart, tz)}</strong><span>{q.serviceNames.join(' + ')}</span><span className="cal-queues__count">{t('calendar:waitlist.inQueue', { count: q.count })}</span></Link>)}
          </div>
        )}
        {selected && business.data && <AppointmentPanel appointment={selected} tz={tz} business={business.data} members={members.data ?? []} onClose={() => setSelected(null)} onChanged={(a) => setSelected(a)} />}
      </div>
      {dialog === 'new' && business.data && <NewAppointmentModal tz={tz} initial={newAt ?? { day }} members={members.data ?? []} onClose={() => setDialog('none')} />}
      {dialog === 'instruction' && <InstructionModal onClose={() => setDialog('none')} />}
      {dialog === 'block' && business.data && <BlockTimeModal tz={tz} day={day} members={members.data ?? []} onClose={() => setDialog('none')} />}
    </>
  );
}
